import { ComputerIcon, MoonIcon, SunIcon } from '@hugeicons/core-free-icons'
import { HugeiconsIcon } from '@hugeicons/react'
import * as React from 'react'

import { type EscolhaTema, escolherTema, useTema } from '@/lib/tema'
import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

type ThemePreference = 'light' | 'dark' | 'system'

function useMounted() {
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => setMounted(true), [])
  return mounted
}

function ThemeMenuIcon({ className, icon }: { className?: string; icon: typeof SunIcon }) {
  return (
    <HugeiconsIcon
      icon={icon}
      strokeWidth={2}
      className={cn('size-4 shrink-0', className)}
      aria-hidden
    />
  )
}

function triggerIconForTheme(theme: string | undefined, resolvedTheme: string | undefined) {
  if (theme === 'system') {
    return ComputerIcon
  }
  return resolvedTheme === 'dark' ? SunIcon : MoonIcon
}

type ThemeToggleProps = Omit<React.ComponentProps<typeof Button>, 'onClick' | 'children'> & {
  /** toggle: claro/escuro; menu: claro, escuro e sistema. */
  mode?: 'toggle' | 'menu'
}

function ThemeToggle({
  mode = 'toggle',
  variant = 'ghost',
  size = 'icon',
  className,
  disabled,
  'aria-label': ariaLabel,
  ...props
}: ThemeToggleProps) {
  // Liga o componente ao tema do app (src/lib/tema.ts), no lugar do next-themes.
  const { escolha, escuro } = useTema()
  const theme = escolha === 'sistema' ? 'system' : escolha === 'escuro' ? 'dark' : 'light'
  const resolvedTheme = escuro ? 'dark' : 'light'
  const setTheme = (valor: string) =>
    escolherTema(
      (valor === 'system' ? 'sistema' : valor === 'dark' ? 'escuro' : 'claro') satisfies EscolhaTema
    )
  const mounted = useMounted()
  const isDark = resolvedTheme === 'dark'
  const preference = (theme as ThemePreference | undefined) ?? 'system'

  const toggleLabel = !mounted
    ? 'Alternar tema'
    : isDark
      ? 'Ativar tema claro'
      : 'Ativar tema escuro'

  if (mode === 'menu') {
    const TriggerIcon = mounted ? triggerIconForTheme(theme, resolvedTheme) : MoonIcon

    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant={variant}
              size={size}
              disabled={disabled ?? !mounted}
              aria-label={ariaLabel ?? 'Escolher tema'}
              className={className}
              {...props}
            />
          }
        >
          <ThemeMenuIcon icon={TriggerIcon} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-40">
          <DropdownMenuRadioGroup value={preference} onValueChange={(value) => setTheme(value)}>
            <DropdownMenuLabel>Tema</DropdownMenuLabel>
            <DropdownMenuRadioItem value="light">
              <ThemeMenuIcon icon={SunIcon} />
              Claro
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark">
              <ThemeMenuIcon icon={MoonIcon} />
              Escuro
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="system">
              <ThemeMenuIcon icon={ComputerIcon} />
              Sistema
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      disabled={disabled ?? !mounted}
      className={className}
      aria-label={ariaLabel ?? toggleLabel}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      {...props}
    >
      {!mounted ? (
        <span className="size-4" aria-hidden />
      ) : isDark ? (
        <ThemeMenuIcon icon={SunIcon} />
      ) : (
        <ThemeMenuIcon icon={MoonIcon} />
      )}
    </Button>
  )
}

export { ThemeToggle }
export type { ThemeToggleProps }
